<template>
  <div class="flex h-auto bg-gray-900 text-white">
    <!-- Sidebar -->
    <aside class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg h-auto bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700">
      <div class="flex items-center justify-center space-x-3">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20">
      </div>
      <Navbar />
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-2xl font-semibold flex items-center space-x-4">
          <span>Manage Cloud Server - ID: {{ serverId }}</span>

    <!-- Floating Actions Buttons -->
    <div class="flex space-x-2">
      <button
        v-if="selectedServer.status === 'Stopped'"
        @click="startServer"
        class="px-3 py-1 rounded bg-green-600 hover:bg-green-700 text-sm transition">
        Start
      </button>
      <button
        v-if="selectedServer.status === 'Running'"
        @click="stopServer"
        class="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-sm transition">
        Stop
      </button>
      <button
        v-if="selectedServer.status === 'Running'"
        @click="restartServer"
        class="px-3 py-1 rounded bg-yellow-500 text-black font-semibold hover:bg-yellow-600 text-sm transition">
        Restart
      </button>
    
      <!-- New Reinstall Button -->
      <button
        v-if="selectedServer.status === 'Running' || selectedServer.status === 'Stopped'"
        @click="showConfirm('Are you sure you want to reinstall this server? All data will be wiped.', reinstallServer)"
        class="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-sm transition">
        Reinstall
      </button>
    
      <button
        v-if="selectedServer.status === 'Running' || selectedServer.status === 'Stopped'"
        @click="showConfirm('Are you sure you want to permanently delete this server?', destroyServer)"
        class="px-3 py-1 rounded bg-gray-600 hover:bg-gray-700 text-sm transition">
        Destroy
      </button>
    </div>
  </h3>

        <!-- Status badge -->
        <span
          class="px-3 py-1 rounded-full text-sm font-medium border"
          :class="selectedServer.status === 'Running'
            ? 'bg-green-600/20 text-green-400 border-green-700'
            : 'bg-red-600/20 text-red-400 border-red-700'">
          {{ selectedServer.status || 'Unknown' }}
        </span>
      </div>

      <!-- Grid -->
      <div class="grid grid-cols-3 gap-6">
        <!-- Left content -->
        <div class="col-span-3 xl:col-span-2 flex flex-col space-y-6">
          <!-- Tabs -->
          <div class="flex space-x-6 border-b border-gray-700">
            <button @click="activeTab = 'dashboard'"
                    class="pb-2 border-b-4 -mb-[1px] transition"
                    :class="activeTab === 'dashboard' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-200'">
              Dashboard
            </button>
            <button @click="activeTab = 'logs'"
                    class="pb-2 border-b-4 -mb-[1px] transition"
                    :class="activeTab === 'logs' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-gray-200'">
              Logs
            </button>
            <button @click="activeTab = 'settings'"
                    class="pb-2 border-b-4 -mb-[1px] transition"
                    :class="activeTab === 'settings' ? 'border-yellow-400 text-yellow-400' : 'border-transparent text-gray-400 hover:text-gray-200'">
              Settings
            </button>
          </div>

          <!-- Dashboard -->
          <div v-if="activeTab === 'dashboard'" class="space-y-6">
            <!-- Stats -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="p-4 bg-gray-800 border border-gray-700 rounded-lg shadow text-center">
                <p class="text-gray-400 text-sm">CPU</p>
                <p class="text-lg font-semibold mt-1">{{ selectedServer.cpu }}</p>
              </div>
              <div class="p-4 bg-gray-800 border border-gray-700 rounded-lg shadow text-center">
                <p class="text-gray-400 text-sm">RAM</p>
                <p class="text-lg font-semibold mt-1">{{ selectedServer.ram }}</p>
              </div>
              <div class="p-4 bg-gray-800 border border-gray-700 rounded-lg shadow text-center">
                <p class="text-gray-400 text-sm">Disk</p>
                <p class="text-lg font-semibold mt-1">{{ selectedServer.disk || '--' }}</p>
              </div>
            </div>

            <!-- Performance -->
            <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg">
              <h4 class="text-lg font-semibold mb-4">Performance</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p class="text-gray-400 mb-2">CPU Usage</p>
                  <div class="w-full bg-gray-700 h-3 rounded-lg overflow-hidden">
                    <div class="h-3 bg-blue-500" :style="{ width: selectedServer.cpuusage + '%' }"></div>
                  </div>
                  <p class="mt-2 text-xs text-gray-400">{{ selectedServer.cpuusage }}%</p>
                </div>
                <div>
                  <p class="text-gray-400 mb-2">RAM Usage</p>
                  <div class="w-full bg-gray-700 h-3 rounded-lg overflow-hidden">
                    <div class="h-3 bg-green-500" :style="{ width: selectedServer.ramusage + '%' }"></div>
                  </div>
                  <p class="mt-2 text-xs text-gray-400">{{ selectedServer.ramusage }}%</p>
                </div>
              </div>
            </div>

            <!-- Network Chart -->
            <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg">
              <h4 class="text-lg font-semibold mb-4">Network</h4>
              <div class="h-64">
                <network-chart :inData="network.inbound" :outData="network.outbound" :labels="network.labels" />
              </div>
            </div>
          </div>

          <!-- Logs -->
          <div v-if="activeTab === 'logs'" class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg">
            <div class="flex items-center justify-between mb-4">
              <h4 class="text-lg font-semibold">Activity Logs</h4>
              <button class="text-sm px-3 py-1 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700" @click="refreshLogs">
                Refresh
              </button>
            </div>
            <ul class="space-y-2 text-gray-300 max-h-[28rem] overflow-y-auto pr-2">
              <li v-for="(log, idx) in logs" :key="idx" class="border-b border-gray-800 pb-2">
                {{ log }}
              </li>
              <li v-if="!logs.length" class="text-gray-500 italic">No logs found.</li>
            </ul>
          </div>

          <!-- Settings -->
          <div v-if="activeTab === 'settings'" class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg">
            <h4 class="text-lg font-semibold mb-4">Server Settings</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm text-gray-400 mb-1">Server Name</label>
                <input
                  v-model="selectedServer.name"
                  class="w-full px-3 py-2 rounded bg-gray-900 border border-gray-700 text-white focus:border-blue-500 focus:ring focus:ring-blue-500/30" />
              </div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Auto Restart</label>
                <select
                  v-model="selectedServer.autoRestart"
                  class="w-full px-3 py-2 rounded bg-gray-900 border border-gray-700 text-white focus:border-blue-500 focus:ring focus:ring-blue-500/30">
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Notes</label>
                <textarea
                  v-model="settings.notes"
                  rows="3"
                  class="w-full px-3 py-2 rounded bg-gray-900 border border-gray-700 text-white focus:border-blue-500 focus:ring focus:ring-blue-500/30"></textarea>
              </div>
            </div>
            <button @click="saveSettings" class="mt-4 px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700">
              Save Changes
            </button>
          </div>
        </div>

        <!-- Right Summary -->
        <div class="hidden xl:block col-span-1">
          <div class="sticky top-6 space-y-6">
            <!-- Instance Summary -->
            <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg">
              <h4 class="text-lg font-semibold mb-4">Instance Summary</h4>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between"><span class="text-gray-400">Name</span><span>{{ selectedServer.servername || '—' }}</span></div>
                <div class="flex justify-between"><span class="text-gray-400">IP Address</span><span>{{ selectedServer.ipaddress || '—' }}</span></div>
                <div class="flex justify-between"><span class="text-gray-400">Region</span><span>{{ selectedServer.location || '—' }}</span></div>
                <div class="flex justify-between"><span class="text-gray-400">CPU</span><span>{{ selectedServer.cpu }}</span></div>
                <div class="flex justify-between"><span class="text-gray-400">RAM</span><span>{{ selectedServer.ram }}</span></div>
                <div class="flex justify-between"><span class="text-gray-400">Disk</span><span>{{ selectedServer.disk || '—' }}</span></div>
                <div class="flex justify-between"><span class="text-gray-400">Status</span>
                  <span :class="selectedServer.status === 'Running' ? 'text-green-400' : 'text-red-400'">
                    {{ selectedServer.status || '—' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Recent Events -->
            <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg">
              <h4 class="text-lg font-semibold mb-4">Recent Events</h4>
              <ul class="space-y-2 text-sm max-h-48 overflow-y-auto pr-1">
                <li v-for="(log, i) in logs.slice(0, 6)" :key="i" class="text-gray-300 border-b border-gray-800 pb-2">
                  {{ log }}
                </li>
                <li v-if="!logs.length" class="text-gray-500 italic">No recent events.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ToastManager ref="toastManager" />

      <!-- Confirmation Modal -->
      <div v-if="showModal" class="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
        <div class="bg-gray-800 rounded-lg p-6 w-96">
          <h4 class="text-lg font-semibold mb-4 text-white">Confirm Action</h4>
          <p class="text-gray-300 mb-6">{{ confirmMessage }}</p>
          <div class="flex justify-end space-x-4">
            <button @click="cancelConfirm" class="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white">Cancel</button>
            <button @click="executeConfirm" class="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white">Confirm</button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Navbar from '@/components/nav.vue';
import ToastManager from '@/components/ToastManager.vue';
import NetworkChart from '@/components/NetworkChart.vue';
import axios from 'axios';

export default {
  name: "ServerManagementPage",
  props: ['serverId'],
  components: { Navbar, ToastManager, NetworkChart },
  data() {
    return {
      selectedServer: {
        status: 'Stopped',
        cpu: '4 vCPU',
        ram: '2 GB',
        disk: '50 GB',
        location: '',
        servername: 'My Server',
        autoRestart: 'enabled',
        cpuusage: 50,
        ramusage: 50,
        ipaddress: '127.0.0.1',
      },
      logs: [],
      activeTab: 'dashboard',
      settings: { notes: '' },
      loading: true,
      network: {
        labels: ["10:00", "10:05", "10:10", "10:15", "10:20"],
        inbound: [12, 19, 7, 15, 22],
        outbound: [8, 11, 15, 9, 14],
      },
      showModal: false,
      confirmMessage: '',
      confirmAction: null,
    };
  },
  async created() {
    await this.fetchServerDetails();
  },
  methods: {
    async fetchServerDetails() {
      try {
        const response = await axios.get(`https://api.syniac.com:3000/api/server/server/${this.serverId}`, { withCredentials: true });
        this.selectedServer = { ...this.selectedServer, ...response.data };
        const logs = await axios.get(`https://api.syniac.com:3000/api/server/server-logs/${this.serverId}`, { withCredentials: true });
        this.logs = (logs.data.logs || []).reverse();
      } catch (error) {
        console.error("Error fetching server details:", error);
      } finally {
        this.loading = false;
      }
    },
    refreshLogs() {
      this.fetchServerDetails();
    },
    async startServer() {
      try {
        await axios.post(`https://api.syniac.com:3000/api/server/${this.serverId}/start`, {}, { withCredentials: true });
        this.selectedServer.status = 'Running';
        this.refreshLogs();
        this.$refs.toastManager.addToast('Server started!', 'success');
      } catch (err) {
        console.error(err);
        this.$refs.toastManager.addToast('Failed to start server', 'error');
      }
    },
    async stopServer() {
      try {
        await axios.post(`https://api.syniac.com:3000/api/server/${this.serverId}/stop`, {}, { withCredentials: true });
        this.selectedServer.status = 'Stopped';
        this.refreshLogs();
        this.$refs.toastManager.addToast('Server stopped!', 'error');
      } catch (err) {
        console.error(err);
        this.$refs.toastManager.addToast('Failed to stop server', 'error');
      }
    },
    async restartServer() {
      try {
        await axios.post(`https://api.syniac.com:3000/api/server/${this.serverId}/restart`, {}, { withCredentials: true });
        this.selectedServer.status = 'Running';
        this.refreshLogs();
        this.$refs.toastManager.addToast('Server restarted!', 'info');
      } catch (err) {
        console.error(err);
        this.$refs.toastManager.addToast('Failed to restart server', 'error');
      }
    },
    async reinstallServer() {
      try {
        await axios.post(`https://api.syniac.com:3000/api/server/${this.serverId}/reinstall`, {}, { withCredentials: true });
        this.$refs.toastManager.addToast('Server reinstalled!', 'info');
        this.refreshLogs();
      } catch (err) {
        console.error(err);
        this.$refs.toastManager.addToast('Failed to reinstall server', 'error');
      }
    },
    showConfirm(message, action) {
      this.confirmMessage = message;
      this.confirmAction = action;
      this.showModal = true;
    },
    cancelConfirm() {
      this.showModal = false;
      this.confirmMessage = '';
      this.confirmAction = null;
    },
    executeConfirm() {
      if (this.confirmAction) this.confirmAction();
      this.cancelConfirm();
    },
    async destroyServer() {
      try {
        await axios.delete(`https://api.syniac.com:3000/api/server/delete/${this.serverId}`, { withCredentials: true });
        this.$refs.toastManager.addToast('Server destroyed!', 'error');
        this.selectedServer.status = 'Deleted';
        this.$router.push(`/servers`);
      } catch (err) {
        console.error(err);
        this.$refs.toastManager.addToast('Failed to destroy server', 'error');
      }
    },
    saveSettings() {
      this.logs.unshift(`Settings saved at ${new Date().toLocaleString()}`);
      this.$refs.toastManager.addToast('Settings saved', 'success');
    }
  }
};
</script>

